/**
 * ESG Privacy Assessment Component
 * 
 * Comprehensive privacy assessment dashboard implementing industry-standard privacy frameworks.
 * Provides detailed analysis of privacy compliance, risk assessment, and governance metrics.
 * 
 * Features:
 * - Privacy Score Overview with multi-dimensional scoring
 * - Component Analysis across 8 key privacy areas
 * - Risk Assessment with privacy-specific risk matrix
 * - Compliance Status for GDPR, CCPA, ISO 27701, NIST Privacy Framework
 * - Data Processing Activities tracking and DPIA status
 * - Consent Management metrics and trends
 * - Privacy Governance metrics and structure
 * - Incident Tracking and response metrics
 * - Industry Benchmarking and peer comparison
 * - Recommendations for privacy enhancement
 * - Trend Analysis and emerging privacy risks
 * 
 * @version 1.0.0
 * @created 2025-09-23
 */

import React, { useState, useEffect } from 'react';
import esgPrivacyAssessmentService from '../../../services/esg/esgPrivacyAssessmentService';
import './ESGPrivacyAssessment.css';

const ESGPrivacyAssessment = () => {
  const [privacyData, setPrivacyData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Initial data load
    const loadPrivacyData = () => {
      try {
        const data = esgPrivacyAssessmentService.getPrivacyAssessment();
        setPrivacyData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading privacy assessment data:', error);
        setLoading(false);
      }
    };

    loadPrivacyData();

    // Subscribe to updates
    const unsubscribe = esgPrivacyAssessmentService.subscribe((updatedData) => {
      setPrivacyData(updatedData);
    });

    return () => unsubscribe();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await esgPrivacyAssessmentService.refreshPrivacyData();
    } catch (error) {
      console.error('Error refreshing privacy data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return '#10B981'; // Green
    if (score >= 70) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low': return '#10B981';
      case 'medium': return '#F59E0B';
      case 'high': return '#EF4444';
      case 'critical': return '#DC2626';
      default: return '#6B7280';
    }
  };

  const getComplianceColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'compliant': return '#10B981';
      case 'substantially compliant': return '#F59E0B';
      case 'non-compliant': return '#EF4444';
      default: return '#6B7280';
    }
  };

  if (loading) {
    return (
      <div className="privacy-assessment-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading Privacy Assessment...</p>
        </div>
      </div>
    );
  }

  if (!privacyData) {
    return (
      <div className="privacy-assessment-container">
        <div className="error-message">
          <h2>Error Loading Privacy Assessment</h2>
          <p>Unable to load privacy assessment data. Please try again.</p>
          <button onClick={handleRefresh} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  const renderOverview = () => (
    <div className="tab-content">
      <div className="overview-header">
        <div className="privacy-score-card">
          <h2>Overall Privacy Score</h2>
          <div className="score-display">
            <div 
              className="score-circle" 
              style={{ 
                background: `conic-gradient(${getScoreColor(privacyData.overview.overallScore)} ${privacyData.overview.overallScore * 3.6}deg, #e5e7eb 0deg)` 
              }}
            >
              <div className="score-inner">
                <span className="score-number">{privacyData.overview.overallScore}</span>
                <span className="score-grade">{privacyData.overview.grade}</span>
              </div>
            </div>
          </div>
          <div className="score-details">
            <p><strong>Risk Level:</strong> <span style={{color: getRiskColor(privacyData.overview.riskLevel)}}>{privacyData.overview.riskLevel}</span></p>
            <p><strong>Last Assessment:</strong> {new Date(privacyData.overview.lastAssessment).toLocaleDateString()}</p>
            <p><strong>Next Review:</strong> {new Date(privacyData.overview.nextReview).toLocaleDateString()}</p>
          </div>
        </div>
        
        <div className="compliance-status-card">
          <h3>Compliance Status</h3>
          <div className="compliance-grid">
            <div className="compliance-item">
              <span className="compliance-label">GDPR:</span>
              <span 
                className="compliance-status"
                style={{color: getComplianceColor(privacyData.overview.complianceStatus.gdpr)}}
              >
                {privacyData.overview.complianceStatus.gdpr}
              </span>
            </div>
            <div className="compliance-item">
              <span className="compliance-label">CCPA:</span>
              <span 
                className="compliance-status"
                style={{color: getComplianceColor(privacyData.overview.complianceStatus.ccpa)}}
              >
                {privacyData.overview.complianceStatus.ccpa}
              </span>
            </div>
            <div className="compliance-item">
              <span className="compliance-label">ISO 27701:</span>
              <span 
                className="compliance-status"
                style={{color: getComplianceColor(privacyData.overview.complianceStatus.iso27701)}}
              >
                {privacyData.overview.complianceStatus.iso27701}
              </span>
            </div>
            <div className="compliance-item">
              <span className="compliance-label">Overall:</span>
              <span 
                className="compliance-status"
                style={{color: getComplianceColor(privacyData.overview.complianceStatus.overall)}}
              >
                {privacyData.overview.complianceStatus.overall}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="key-metrics-grid">
        <div className="metric-card">
          <h4>Data Processing Activities</h4>
          <div className="metric-value">{privacyData.dataProcessing.summary.totalActivities}</div>
          <div className="metric-label">Total Activities</div>
          <div className="metric-subtext">
            {privacyData.dataProcessing.summary.dpiaCompleted}/{privacyData.dataProcessing.summary.dpiaRequired} DPIAs Completed
          </div>
        </div>
        
        <div className="metric-card">
          <h4>Active Consents</h4>
          <div className="metric-value">{(privacyData.consentManagement.overview.totalConsents / 1000000).toFixed(1)}M</div>
          <div className="metric-label">Total Consents</div>
          <div className="metric-subtext">
            {privacyData.consentManagement.overview.consentRate}% Consent Rate
          </div>
        </div>
        
        <div className="metric-card">
          <h4>Privacy Incidents</h4>
          <div className="metric-value">{privacyData.incidents.summary.total2025}</div>
          <div className="metric-label">Total in 2025</div>
          <div className="metric-subtext">
            {privacyData.incidents.summary.resolved} Resolved
          </div>
        </div>
        
        <div className="metric-card">
          <h4>Training Completion</h4>
          <div className="metric-value">{privacyData.governanceMetrics.training.generalPrivacyTraining.completion}%</div>
          <div className="metric-label">General Training</div>
          <div className="metric-subtext">
            Score: {privacyData.governanceMetrics.training.generalPrivacyTraining.averageScore}
          </div>
        </div>
      </div>
    </div>
  );

  const renderComponents = () => (
    <div className="tab-content">
      <h2>Privacy Components Analysis</h2>
      <p>Detailed breakdown of privacy assessment across 8 key components</p>
      
      <div className="components-grid">
        {Object.entries(privacyData.components).map(([key, component]) => (
          <div key={key} className="component-card">
            <div className="component-header">
              <h3>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h3>
              <div className="component-score" style={{color: getScoreColor(component.score)}}>
                {component.score}/100
              </div>
            </div>
            <div className="component-details">
              <div className="component-weight">Weight: {component.weight}%</div>
              <div className="component-status" style={{color: getScoreColor(component.score)}}>
                {component.status}
              </div>
              <p className="component-description">{component.description}</p>
              
              <div className="component-breakdown">
                <h4>Component Breakdown:</h4>
                {Object.entries(component.details).map(([subKey, value]) => (
                  <div key={subKey} className="breakdown-item">
                    <span className="breakdown-label">
                      {subKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </span>
                    <span className="breakdown-value" style={{color: getScoreColor(value)}}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRiskAssessment = () => (
    <div className="tab-content">
      <h2>Privacy Risk Assessment</h2>
      <p>Comprehensive risk analysis and mitigation strategies</p>
      
      <div className="risk-section">
        <h3>Risk Matrix</h3>
        <div className="risk-matrix">
          <table className="risk-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Likelihood</th>
                <th>Impact</th>
                <th>Risk Level</th>
                <th>Mitigation</th>
              </tr>
            </thead>
            <tbody>
              {privacyData.riskAssessment.riskMatrix.map((risk, index) => (
                <tr key={index}>
                  <td>{risk.category}</td>
                  <td>{risk.likelihood}</td>
                  <td>{risk.impact}</td>
                  <td style={{color: getRiskColor(risk.riskLevel)}}>{risk.riskLevel}</td>
                  <td>{risk.mitigation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="risk-section">
        <h3>Highest Priority Risks</h3>
        <div className="high-risks-grid">
          {privacyData.riskAssessment.highestRisks.map((risk, index) => (
            <div key={index} className="risk-card">
              <div className="risk-header">
                <h4>{risk.risk}</h4>
                <div className="risk-score" style={{color: getRiskColor('medium')}}>
                  Score: {risk.score}
                </div>
              </div>
              <p className="risk-description">{risk.description}</p>
              <div className="risk-recommendations">
                <h5>Recommendations:</h5>
                <ul>
                  {risk.recommendations.map((rec, recIndex) => (
                    <li key={recIndex}>{rec}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="risk-section">
        <h3>Risk Trends</h3>
        <div className="trends-grid">
          <div className="trend-card decreasing">
            <h4>Decreasing Risks</h4>
            <ul>
              {privacyData.riskAssessment.riskTrends.decreasing.map((risk, index) => (
                <li key={index}>{risk}</li>
              ))}
            </ul>
          </div>
          <div className="trend-card stable">
            <h4>Stable Risks</h4>
            <ul>
              {privacyData.riskAssessment.riskTrends.stable.map((risk, index) => (
                <li key={index}>{risk}</li>
              ))}
            </ul>
          </div>
          <div className="trend-card increasing">
            <h4>Increasing Risks</h4>
            <ul>
              {privacyData.riskAssessment.riskTrends.increasing.map((risk, index) => (
                <li key={index}>{risk}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCompliance = () => (
    <div className="tab-content">
      <h2>Compliance Framework Status</h2>
      <p>Detailed compliance assessment across major privacy frameworks</p>
      
      <div className="compliance-frameworks">
        {Object.entries(privacyData.complianceFrameworks).map(([key, framework]) => (
          <div key={key} className="framework-card">
            <div className="framework-header">
              <h3>{framework.name}</h3>
              <div className="framework-score" style={{color: getScoreColor(framework.score)}}>
                {framework.score}/100
              </div>
            </div>
            <div className="framework-status" style={{color: getComplianceColor(framework.status)}}>
              Status: {framework.status}
            </div>
            <div className="framework-assessment">
              Last Assessment: {new Date(framework.lastAssessment).toLocaleDateString()}
            </div>
            
            <div className="requirements-grid">
              <h4>Requirements Status:</h4>
              {Object.entries(framework.requirements).map(([reqKey, reqStatus]) => (
                <div key={reqKey} className="requirement-item">
                  <span className="requirement-label">
                    {reqKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                  </span>
                  <span 
                    className="requirement-status"
                    style={{color: getComplianceColor(reqStatus)}}
                  >
                    {reqStatus}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDataProcessing = () => (
    <div className="tab-content">
      <h2>Data Processing Assessment</h2>
      <p>Analysis of data processing activities and DPIA status</p>
      
      <div className="processing-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <div className="stat-value">{privacyData.dataProcessing.summary.totalActivities}</div>
            <div className="stat-label">Total Activities</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{privacyData.dataProcessing.summary.highRiskActivities}</div>
            <div className="stat-label">High Risk</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">
              {privacyData.dataProcessing.summary.dpiaCompleted}/{privacyData.dataProcessing.summary.dpiaRequired}
            </div>
            <div className="stat-label">DPIAs Complete</div>
          </div>
        </div>
      </div>

      <div className="activities-section">
        <h3>Key Processing Activities</h3>
        <div className="activities-grid">
          {privacyData.dataProcessing.activities.map((activity) => (
            <div key={activity.id} className="activity-card">
              <div className="activity-header">
                <h4>{activity.name}</h4>
                <span className={`risk-badge ${activity.riskLevel.toLowerCase()}`}>
                  {activity.riskLevel}
                </span>
              </div>
              <div className="activity-details">
                <p><strong>Purpose:</strong> {activity.purpose}</p>
                <p><strong>Legal Basis:</strong> {activity.legalBasis}</p>
                <p><strong>DPIA Status:</strong> 
                  <span className={activity.dpiaStatus === 'Completed' ? 'status-complete' : 'status-pending'}>
                    {activity.dpiaStatus}
                  </span>
                </p>
                <p><strong>Retention:</strong> {activity.retentionPeriod}</p>
                <div className="data-categories">
                  <strong>Data Categories:</strong>
                  <div className="categories-list">
                    {activity.dataCategories.map((category, index) => (
                      <span key={index} className="category-tag">{category}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="data-flows-section">
        <h3>Data Flow Mapping</h3>
        <div className="flows-grid">
          <div className="flow-card">
            <h4>Inbound Data Sources</h4>
            <ul>
              {privacyData.dataProcessing.dataFlows.inbound.map((source, index) => (
                <li key={index}>{source}</li>
              ))}
            </ul>
          </div>
          <div className="flow-card">
            <h4>Processing Systems</h4>
            <ul>
              {privacyData.dataProcessing.dataFlows.processing.map((system, index) => (
                <li key={index}>{system}</li>
              ))}
            </ul>
          </div>
          <div className="flow-card">
            <h4>Outbound Destinations</h4>
            <ul>
              {privacyData.dataProcessing.dataFlows.outbound.map((destination, index) => (
                <li key={index}>{destination}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderConsentManagement = () => (
    <div className="tab-content">
      <h2>Consent Management</h2>
      <p>Analysis of consent collection, management, and withdrawal patterns</p>
      
      <div className="consent-overview">
        <div className="consent-metrics">
          <div className="metric-item">
            <div className="metric-value">
              {(privacyData.consentManagement.overview.totalConsents / 1000000).toFixed(1)}M
            </div>
            <div className="metric-label">Total Consents</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">{privacyData.consentManagement.overview.consentRate}%</div>
            <div className="metric-label">Consent Rate</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">{privacyData.consentManagement.overview.withdrawalRate}%</div>
            <div className="metric-label">Withdrawal Rate</div>
          </div>
        </div>
      </div>

      <div className="consent-types-section">
        <h3>Consent by Type</h3>
        <div className="consent-types-grid">
          {privacyData.consentManagement.consentTypes.map((type, index) => (
            <div key={index} className="consent-type-card">
              <h4>{type.type}</h4>
              <div className="consent-stats">
                <div className="stat-row">
                  <span>Consents:</span>
                  <span>{type.consents.toLocaleString()}</span>
                </div>
                <div className="stat-row">
                  <span>Consent Rate:</span>
                  <span style={{color: getScoreColor(type.consentRate * 1.2)}}>{type.consentRate}%</span>
                </div>
                <div className="stat-row">
                  <span>Withdrawals:</span>
                  <span>{type.withdrawals.toLocaleString()}</span>
                </div>
                <div className="stat-row">
                  <span>Withdrawal Rate:</span>
                  <span>{type.withdrawalRate}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="consent-mechanism-section">
        <h3>Consent Mechanism Details</h3>
        <div className="mechanism-grid">
          {Object.entries(privacyData.consentManagement.consentMechanism).map(([key, value]) => (
            <div key={key} className="mechanism-item">
              <span className="mechanism-label">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
              </span>
              <span className="mechanism-value">{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="consent-trends-section">
        <h3>Withdrawal Reasons Analysis</h3>
        <div className="reasons-chart">
          {privacyData.consentManagement.recentTrends.withdrawalReasons.map((reason, index) => (
            <div key={index} className="reason-bar">
              <div className="reason-label">{reason.reason}</div>
              <div className="reason-progress">
                <div 
                  className="reason-fill" 
                  style={{width: `${reason.percentage}%`}}
                ></div>
              </div>
              <div className="reason-percentage">{reason.percentage}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderGovernance = () => (
    <div className="tab-content">
      <h2>Privacy Governance</h2>
      <p>Overview of privacy governance structure and metrics</p>
      
      <div className="governance-structure">
        <h3>Governance Structure</h3>
        <div className="structure-grid">
          {Object.entries(privacyData.governanceMetrics.structure).map(([key, value]) => (
            <div key={key} className="structure-item">
              <span className="structure-label">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
              </span>
              <span className="structure-value">{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="policies-section">
        <h3>Policy Status</h3>
        <div className="policies-grid">
          {Object.entries(privacyData.governanceMetrics.policies).map(([key, policy]) => (
            <div key={key} className="policy-card">
              <h4>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</h4>
              <div className="policy-status" style={{color: getComplianceColor(policy.status)}}>
                Status: {policy.status}
              </div>
              <div className="policy-dates">
                <div>Last Update: {new Date(policy.lastUpdate).toLocaleDateString()}</div>
                <div>Next Review: {new Date(policy.nextReview).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="training-section">
        <h3>Training Programs</h3>
        <div className="training-grid">
          <div className="training-card">
            <h4>General Privacy Training</h4>
            <div className="training-metrics">
              <div className="metric-row">
                <span>Completion Rate:</span>
                <span style={{color: getScoreColor(privacyData.governanceMetrics.training.generalPrivacyTraining.completion)}}>
                  {privacyData.governanceMetrics.training.generalPrivacyTraining.completion}%
                </span>
              </div>
              <div className="metric-row">
                <span>Average Score:</span>
                <span style={{color: getScoreColor(privacyData.governanceMetrics.training.generalPrivacyTraining.averageScore)}}>
                  {privacyData.governanceMetrics.training.generalPrivacyTraining.averageScore}
                </span>
              </div>
            </div>
          </div>
          <div className="training-card">
            <h4>Specialized Training</h4>
            <div className="training-metrics">
              <div className="metric-row">
                <span>Completion Rate:</span>
                <span style={{color: getScoreColor(privacyData.governanceMetrics.training.specializedTraining.completion)}}>
                  {privacyData.governanceMetrics.training.specializedTraining.completion}%
                </span>
              </div>
              <div className="metric-row">
                <span>Average Score:</span>
                <span style={{color: getScoreColor(privacyData.governanceMetrics.training.specializedTraining.averageScore)}}>
                  {privacyData.governanceMetrics.training.specializedTraining.averageScore}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="assessments-section">
        <h3>Assessment Programs</h3>
        <div className="assessments-grid">
          <div className="assessment-card">
            <h4>Privacy Impact Assessments</h4>
            <div className="assessment-metrics">
              <div className="metric-row">
                <span>Conducted:</span>
                <span>{privacyData.governanceMetrics.assessments.privacyImpactAssessments.conducted}</span>
              </div>
              <div className="metric-row">
                <span>Required:</span>
                <span>{privacyData.governanceMetrics.assessments.privacyImpactAssessments.required}</span>
              </div>
              <div className="metric-row">
                <span>Average Score:</span>
                <span style={{color: getScoreColor(privacyData.governanceMetrics.assessments.privacyImpactAssessments.averageScore * 10)}}>
                  {privacyData.governanceMetrics.assessments.privacyImpactAssessments.averageScore}/10
                </span>
              </div>
            </div>
          </div>
          <div className="assessment-card">
            <h4>Vendor Assessments</h4>
            <div className="vendor-risk-breakdown">
              <div className="risk-item high-risk">
                <span>High Risk:</span>
                <span>{privacyData.governanceMetrics.assessments.vendorAssessments.highRisk}</span>
              </div>
              <div className="risk-item medium-risk">
                <span>Medium Risk:</span>
                <span>{privacyData.governanceMetrics.assessments.vendorAssessments.mediumRisk}</span>
              </div>
              <div className="risk-item low-risk">
                <span>Low Risk:</span>
                <span>{privacyData.governanceMetrics.assessments.vendorAssessments.lowRisk}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderIncidents = () => (
    <div className="tab-content">
      <h2>Privacy Incidents</h2>
      <p>Tracking and analysis of privacy incidents and response metrics</p>
      
      <div className="incidents-summary">
        <div className="summary-metrics">
          <div className="metric-item">
            <div className="metric-value">{privacyData.incidents.summary.total2025}</div>
            <div className="metric-label">Total Incidents 2025</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">{privacyData.incidents.summary.resolved}</div>
            <div className="metric-label">Resolved</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">{privacyData.incidents.summary.pending}</div>
            <div className="metric-label">Pending</div>
          </div>
          <div className="metric-item">
            <div className="metric-value">{privacyData.incidents.summary.averageResolutionTime}</div>
            <div className="metric-label">Avg Resolution Time</div>
          </div>
        </div>
      </div>

      <div className="incidents-list">
        <h3>Recent Incidents</h3>
        <div className="incidents-grid">
          {privacyData.incidents.incidents.map((incident) => (
            <div key={incident.id} className="incident-card">
              <div className="incident-header">
                <h4>{incident.id}</h4>
                <span className={`severity-badge ${incident.severity.toLowerCase()}`}>
                  {incident.severity}
                </span>
              </div>
              <div className="incident-details">
                <div className="detail-row">
                  <span>Date:</span>
                  <span>{new Date(incident.date).toLocaleDateString()}</span>
                </div>
                <div className="detail-row">
                  <span>Type:</span>
                  <span>{incident.type}</span>
                </div>
                <div className="detail-row">
                  <span>Affected Records:</span>
                  <span>{incident.affectedRecords.toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <span>Resolution Time:</span>
                  <span>{incident.resolutionTime}</span>
                </div>
                <div className="detail-row">
                  <span>Regulator Notified:</span>
                  <span className={incident.regulatorNotified ? 'status-yes' : 'status-no'}>
                    {incident.regulatorNotified ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div className="incident-description">
                <p><strong>Description:</strong> {incident.description}</p>
                <p><strong>Lessons Learned:</strong> {incident.lessonsLearned}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBenchmarks = () => (
    <div className="tab-content">
      <h2>Industry Benchmarks</h2>
      <p>Comparison with industry standards and peer organizations</p>
      
      <div className="industry-comparison">
        <h3>Industry Performance</h3>
        <div className="comparison-card">
          <div className="comparison-metric">
            <span className="metric-label">Our Score:</span>
            <span className="metric-value" style={{color: getScoreColor(privacyData.benchmarks.industryComparison.ourScore)}}>
              {privacyData.benchmarks.industryComparison.ourScore}
            </span>
          </div>
          <div className="comparison-metric">
            <span className="metric-label">Industry Average:</span>
            <span className="metric-value">{privacyData.benchmarks.industryComparison.industryAverage}</span>
          </div>
          <div className="comparison-metric">
            <span className="metric-label">Industry Median:</span>
            <span className="metric-value">{privacyData.benchmarks.industryComparison.industryMedian}</span>
          </div>
          <div className="comparison-metric">
            <span className="metric-label">Percentile Rank:</span>
            <span className="metric-value" style={{color: getScoreColor(privacyData.benchmarks.industryComparison.percentile)}}>
              {privacyData.benchmarks.industryComparison.percentile}th
            </span>
          </div>
        </div>
      </div>

      <div className="component-benchmarks">
        <h3>Component Benchmarks</h3>
        <div className="benchmarks-table">
          <table>
            <thead>
              <tr>
                <th>Component</th>
                <th>Our Score</th>
                <th>Industry Average</th>
                <th>Ranking</th>
              </tr>
            </thead>
            <tbody>
              {privacyData.benchmarks.componentBenchmarks.map((benchmark, index) => (
                <tr key={index}>
                  <td>{benchmark.component}</td>
                  <td style={{color: getScoreColor(benchmark.ourScore)}}>{benchmark.ourScore}</td>
                  <td>{benchmark.industryAvg}</td>
                  <td style={{color: getScoreColor(parseInt(benchmark.ranking))}}>{benchmark.ranking}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="peer-comparison">
        <h3>Peer Comparison</h3>
        <div className="peers-grid">
          {privacyData.benchmarks.peerComparison.map((peer, index) => (
            <div key={index} className="peer-card">
              <h4>{peer.company}</h4>
              <div className="peer-score" style={{color: getScoreColor(peer.score)}}>
                {peer.score}
              </div>
              <div className={`gap ${peer.gap >= 0 ? 'positive' : 'negative'}`}>
                {peer.gap >= 0 ? '+' : ''}{peer.gap}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderRecommendations = () => (
    <div className="tab-content">
      <h2>Recommendations</h2>
      <p>Actionable recommendations for privacy enhancement</p>
      
      <div className="recommendations-section">
        <h3>Immediate Actions</h3>
        <div className="recommendations-grid">
          {privacyData.recommendations.immediate.map((rec, index) => (
            <div key={index} className="recommendation-card">
              <div className="rec-header">
                <h4>{rec.category}</h4>
                <span className={`priority-badge ${rec.priority.toLowerCase()}`}>
                  {rec.priority} Priority
                </span>
              </div>
              <div className="rec-content">
                <p><strong>Recommendation:</strong> {rec.recommendation}</p>
                <p><strong>Impact:</strong> {rec.impact}</p>
                <div className="rec-metadata">
                  <span className="effort-badge">Effort: {rec.effort}</span>
                  <span className="timeline-badge">Timeline: {rec.timeline}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="recommendations-section">
        <h3>Strategic Initiatives</h3>
        <div className="strategic-recommendations">
          {privacyData.recommendations.strategic.map((rec, index) => (
            <div key={index} className="strategic-card">
              <h4>{rec.category}</h4>
              <p><strong>Initiative:</strong> {rec.recommendation}</p>
              <p><strong>Expected Impact:</strong> {rec.impact}</p>
              <p><strong>Timeline:</strong> {rec.timeline}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="recommendations-section">
        <h3>Compliance Actions</h3>
        <div className="compliance-recommendations">
          {privacyData.recommendations.compliance.map((rec, index) => (
            <div key={index} className="compliance-rec-card">
              <h4>{rec.regulation}</h4>
              <p><strong>Requirement:</strong> {rec.requirement}</p>
              <div className="current-status" style={{color: getComplianceColor(rec.currentStatus)}}>
                Status: {rec.currentStatus}
              </div>
              <div className="actions-list">
                <strong>Required Actions:</strong>
                <ul>
                  {rec.actions.map((action, actionIndex) => (
                    <li key={actionIndex}>{action}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTrends = () => (
    <div className="tab-content">
      <h2>Trend Analysis</h2>
      <p>Historical trends and emerging privacy risks</p>
      
      <div className="score-history-section">
        <h3>Privacy Score History</h3>
        <div className="score-chart">
          {privacyData.trends.scoreHistory.map((point, index) => (
            <div key={index} className="score-point">
              <div className="score-month">{point.month}</div>
              <div className="score-bar">
                <div 
                  className="score-fill" 
                  style={{height: `${point.score}%`, backgroundColor: getScoreColor(point.score)}}
                ></div>
              </div>
              <div className="score-value">{point.score}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="improvement-areas">
        <h3>Recent Improvements</h3>
        <div className="improvements-grid">
          {privacyData.trends.improvementAreas.map((improvement, index) => (
            <div key={index} className="improvement-card">
              <h4>{improvement.area}</h4>
              <div className="improvement-metric">
                <span className="improvement-value positive">{improvement.improvement}</span>
                <span className="improvement-period">over {improvement.period}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="emerging-risks-section">
        <h3>Emerging Privacy Risks</h3>
        <div className="risks-grid">
          {privacyData.trends.emergingRisks.map((risk, index) => (
            <div key={index} className="emerging-risk-card">
              <h4>{risk.risk}</h4>
              <p className="risk-description">{risk.description}</p>
              <div className="risk-metadata">
                <div className="risk-probability">
                  <span>Probability:</span>
                  <span className={`probability ${risk.probability.toLowerCase()}`}>{risk.probability}</span>
                </div>
                <div className="risk-timeline">
                  <span>Timeline:</span>
                  <span>{risk.timeline}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Privacy Overview', component: renderOverview },
    { id: 'components', label: 'Components', component: renderComponents },
    { id: 'risk', label: 'Risk Assessment', component: renderRiskAssessment },
    { id: 'compliance', label: 'Compliance', component: renderCompliance },
    { id: 'processing', label: 'Data Processing', component: renderDataProcessing },
    { id: 'consent', label: 'Consent Management', component: renderConsentManagement },
    { id: 'governance', label: 'Governance', component: renderGovernance },
    { id: 'incidents', label: 'Incidents', component: renderIncidents },
    { id: 'benchmarks', label: 'Benchmarks', component: renderBenchmarks },
    { id: 'recommendations', label: 'Recommendations', component: renderRecommendations },
    { id: 'trends', label: 'Trends', component: renderTrends }
  ];

  return (
    <div className="privacy-assessment-container">
      <div className="privacy-assessment-header">
        <div className="header-content">
          <h1>ESG Privacy Assessment</h1>
          <p>Comprehensive privacy compliance analysis and risk assessment</p>
          <div className="header-actions">
            <button 
              onClick={handleRefresh} 
              disabled={refreshing}
              className="refresh-button"
            >
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tabs-content">
          {tabs.find(tab => tab.id === activeTab)?.component()}
        </div>
      </div>
    </div>
  );
};

export default ESGPrivacyAssessment;